import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';

const DETAIL_EXPORT_BUTTON_CLASS =
  'inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-900 shadow-sm transition-colors hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50';

export interface KpiBacklogTableModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle: string;
  children: React.ReactNode;
  /** Grouped summary table as .xlsx (same rows as on screen). */
  onExportSummaryExcel?: () => void;
  onExportSummaryPdf?: () => void;
  exportDisabled?: boolean;
  /** Per-ticket detail list as .xlsx (separate from summary). */
  onExportDetailExcel?: () => void | Promise<void>;
  onExportDetailPdf?: () => void | Promise<void>;
  detailExportDisabled?: boolean;
  detailExportLoading?: boolean;
}

const KpiBacklogTableModal: React.FC<KpiBacklogTableModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  onExportSummaryExcel,
  onExportSummaryPdf,
  exportDisabled = false,
  onExportDetailExcel,
  onExportDetailPdf,
  detailExportDisabled = false,
  detailExportLoading = false,
}) => {
  const showSummaryExport = Boolean(onExportSummaryExcel);
  const showDetailExport = Boolean(onExportDetailExcel);

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[100]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/40 transition-opacity" aria-hidden />
        </Transition.Child>

        <div className="fixed inset-0 z-[101] overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-6">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative flex max-h-[90vh] w-full max-w-6xl transform flex-col overflow-hidden rounded-2xl border border-slate-200/90 bg-white text-left shadow-xl shadow-slate-900/15 ring-1 ring-slate-950/[0.04] transition-all">
                <div className="flex shrink-0 flex-col gap-3 border-b border-slate-200 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
                  <div className="min-w-0 pr-8 text-left sm:pr-0">
                    <Dialog.Title as="h2" className="text-lg font-semibold text-slate-900">
                      {title}
                    </Dialog.Title>
                    <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={onClose}
                      className="inline-flex items-center rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                    >
                      <ArrowLeftIcon className="mr-1.5 h-4 w-4" aria-hidden />
                      Back to dashboard
                    </button>
                    {showSummaryExport && (
                      <button
                        type="button"
                        disabled={exportDisabled || detailExportLoading}
                        onClick={() => void onExportSummaryExcel?.()}
                        className="inline-flex items-center rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                        aria-label="Export backlog summary"
                      >
                        Export Summary
                      </button>
                    )}
                    {showDetailExport && (
                      <button
                        type="button"
                        className="inline-flex items-center rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                        disabled={detailExportDisabled || detailExportLoading}
                        onClick={() => void onExportDetailExcel?.()}
                        aria-label="Export backlog detail summary"
                      >
                        {detailExportLoading ? 'Preparing…' : 'Export Detail Summary'}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={onClose}
                      className="rounded-full p-1.5 text-slate-500 hover:bg-slate-100"
                      aria-label="Close"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <div className="min-h-0 flex-1 overflow-auto px-4 py-4 sm:px-5">{children}</div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default KpiBacklogTableModal;
